import { isSequenceApiError, isSequenceApiErrorResponse } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useMutation, useQuery } from '@sequencehq/api/utils'
import { useNotifications } from '@sequencehq/core-components'
import { apiDatesAdapters, format } from '@sequencehq/utils/dates'
import { type ReactNode, useMemo } from 'react'

import { usePermissions } from 'lib/rbac'

import { isDatedBeforeLockDate } from './accountingLockDate.utils'
import { ControlledLockDateModal } from './ControlledLockDateModal'
import { ConfirmationBlocker } from './ConfirmationBlocker'
import type { ValidationItem } from './useSteppedModal'

type Props = {
  entityName: 'invoice' | 'credit note'
  // Callers pass a populated accounting date, so `undefined` means "not loaded
  // yet" (gated by `loading`). The backend is the ultimate lock enforcer.
  entityDate?: string
  /** Blocker title naming the blocked action, e.g. "This invoice can't be sent". */
  blockedTitle: string
  onClose: () => void
}

export const useAccountingLockDateValidation = ({
  entityName,
  entityDate,
  blockedTitle,
  onClose
}: Props): {
  loading: boolean
  validation: ValidationItem[]
  blocker: ReactNode | null
} => {
  const { displayNotification } = useNotifications()
  const { can, isLoading: permissionsLoading } = usePermissions()
  const canEditLockDate = can('org.settings:update')

  const {
    data: accountSettings,
    isLoading: settingsLoading,
    isError: settingsError
  } = useQuery(dashboard20240730Client.getSequenceAccountSettings)
  const lockDate = accountSettings?.accountingLockDate ?? null

  const updateLockDate = useMutation(
    dashboard20240730Client.putSequenceAccountSettings,
    {
      onSuccess: () => {
        displayNotification('Accounting lock date updated', { type: 'success' })
      },
      onError: error => {
        const description =
          isSequenceApiError(error) &&
          isSequenceApiErrorResponse(error.response)
            ? error.response.error.errors?.at(0)?.message
            : undefined

        displayNotification('Accounting lock date failed to update', {
          type: 'error',
          description
        })
      }
    }
  )

  const isBeforeLock = isDatedBeforeLockDate({ entityDate, lockDate })

  const validation = useMemo<ValidationItem[]>(() => {
    if (
      !isBeforeLock ||
      !canEditLockDate ||
      !entityDate ||
      !lockDate ||
      !accountSettings
    ) {
      return []
    }

    return [
      {
        component: ControlledLockDateModal,
        componentProps: {
          entityName,
          entityDate,
          lockDate,
          onClose,
          onSubmit: async (newLockDate: string) => {
            // Moving the lock date is an account-settings change; it doesn't
            // alter the document, so no recalculation is needed.
            await updateLockDate.mutateAsync({
              enabledCurrencies: accountSettings.enabledCurrencies,
              accountingLockDate: newLockDate
            })
          }
        },
        message: `This ${entityName} is dated before the accounting lock date`,
        stepKey: 'ACCOUNTING_LOCK_DATE',
        stepName: 'Accounting lock date'
      } as ValidationItem
    ]
  }, [
    accountSettings,
    canEditLockDate,
    entityDate,
    entityName,
    isBeforeLock,
    lockDate,
    onClose,
    updateLockDate
  ])

  const formatDate = (apiDate: string) => {
    const date = apiDatesAdapters.fromApi(apiDate)

    return date ? format(date, 'd MMM yyyy') : apiDate
  }

  const blocker = (() => {
    // Fail closed: if we couldn't load the lock date we can't tell whether the
    // document is in a locked period, so block rather than silently proceed.
    if (settingsError && entityDate) {
      return (
        <ConfirmationBlocker
          title={blockedTitle}
          onClose={onClose}
          data-testid="common.lockDateBlocker"
        >
          We couldn't verify your accounting lock date. Please try again.
        </ConfirmationBlocker>
      )
    }

    // Users who can't edit the lock date can't remediate inline, so they get a
    // read-only blocker instead of a stepper step.
    if (isBeforeLock && !canEditLockDate && entityDate && lockDate) {
      return (
        <ConfirmationBlocker
          title={blockedTitle}
          onClose={onClose}
          data-testid="common.lockDateBlocker"
        >
          The {entityName} date is before the accounting lock date (
          {formatDate(lockDate)}). Ask an admin to move the lock date.
        </ConfirmationBlocker>
      )
    }

    return null
  })()

  // Gate on permissions loading too: until `getSelf` resolves, `canEditLockDate`
  // reads false, which would briefly show the blocker to a privileged user.
  return { loading: settingsLoading || permissionsLoading, validation, blocker }
}
